import { Link } from "react-router-dom";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { SIGNUP_FORM_URL } from "../external-links";

// ─── Data ────────────────────────────────────────────────────────────────────

const howItFeels = [
  {
    step: "01",
    title: "Choose a perspective",
    text: "Browse skills by topic or author. Install the ones that match your goals. Each skill is built around a distinct methodology and point of view.",
  },
  {
    step: "02",
    title: "Share what you want",
    text: "Every skill declares the data it needs. You decide what it can see from your vault. Nothing more, nothing less.",
  },
  {
    step: "03",
    title: "Run it on your data",
    text: "The skill reads your vault and returns a dashboard with patterns, notes, and questions shaped by that methodology.",
  },
  {
    step: "04",
    title: "Talk to it",
    text: "Open a conversation scoped to that output. Not generic chat — a follow-up grounded in your data and the author's point of view.",
  },
];

const trustPrinciples = [
  {
    title: "You choose the lens",
    text: "Every skill has a named author and a declared methodology. You pick whose perspective you want on your health.",
  },
  {
    title: "You control the data",
    text: "Skills declare what they need. You approve what they see. Your vault stays private — the skill comes to your data, not the other way around.",
  },
  {
    title: "You approve proposals",
    text: "A skill can suggest a new tracker, surface a highlight, or point you to a relevant article. But nothing escapes the sandbox without your say.",
  },
];

const creatorFeatures = [
  {
    title: "Bundle your methodology",
    text: "Package your book, protocol, or framework as a skill. Your readers don't just read your ideas — they explore them through their own data.",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
        <path
          d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Include your library",
    text: "Attach your article index so the skill can surface relevant reading connected to what the user is exploring. Your content meets their context.",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
        <path
          d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "Connect to your practice",
    text: "Let users book a consultation directly from the skill when they want to go deeper. Context can become an appointment.",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path
          d="M16 2v4M8 2v4M3 10h18"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

const exampleSkills = [
  {
    name: "Sleep Deep Dive",
    author: "Dr. Sara Chen",
    description: "Explore 30 nights of sleep data and surface recurring recovery patterns.",
    category: "Sleep",
    tone: "text-kin-lavender",
    badgeTone: "lavender" as const,
  },
  {
    name: "Biomarker Timeline",
    author: "Jonas Weber",
    description: "Organize biomarkers over time and surface changes worth reviewing.",
    category: "Labs",
    tone: "text-kin-green-dark",
    badgeTone: "default" as const,
  },
  {
    name: "Metabolic Flexibility",
    author: "Metabolic Institute",
    description: "Explore glucose variability and meal timing through an energy-focused lens.",
    category: "Metabolic",
    tone: "text-kin-orange",
    badgeTone: "orange" as const,
  },
  {
    name: "Hormone Balance",
    author: "Dr. Nina Larsen",
    description: "Cycle-aware hormone context with practical questions for follow-up.",
    category: "Hormones",
    tone: "text-kin-green-dark",
    badgeTone: "default" as const,
  },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function SectionHeading({
  eyebrow,
  title,
  text,
  align = "left",
}: {
  eyebrow: string;
  title: string;
  text: string;
  align?: "left" | "center";
}) {
  return (
    <div className={align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">{eyebrow}</p>
      <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
        {title}
      </h2>
      <p className="mt-4 text-base leading-relaxed text-kin-body/90 sm:text-lg">{text}</p>
    </div>
  );
}

function SkillAgentDiagram() {
  return (
    <article className="kin-surface kin-surface-clouds relative overflow-hidden rounded-[30px] p-6 sm:p-8">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(58% 48% at 82% 12%, rgba(175,82,222,0.18) 0%, rgba(175,82,222,0) 72%), radial-gradient(52% 46% at 14% 88%, rgba(52,199,89,0.15) 0%, rgba(52,199,89,0) 74%), linear-gradient(160deg, rgba(255,255,255,0.82) 0%, rgba(245,251,247,0.56) 100%)",
        }}
      />
      <div className="relative z-10">
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-kin-muted">
          How a Skill Works
        </p>

        <div className="mt-6 flex flex-col items-center gap-3">
          {/* Author layer */}
          <div className="flex w-full max-w-md items-center gap-3 rounded-2xl border border-kin-lavender/18 bg-kin-white/80 px-4 py-3 backdrop-blur-sm">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-kin-lavender/12 text-kin-lavender">
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-kin-black">Expert Methodology</p>
              <p className="text-xs text-kin-muted">Articles, protocols, clinical framework</p>
            </div>
          </div>

          {/* Connector */}
          <div className="flex flex-col items-center gap-0.5">
            <div className="h-4 w-px bg-kin-olive/20" />
            <svg viewBox="0 0 12 8" className="h-2 w-3 text-kin-olive/30">
              <path d="M1 1l5 5 5-5" stroke="currentColor" fill="none" strokeWidth="1.5" />
            </svg>
          </div>

          {/* Agent layer */}
          <div className="flex w-full max-w-md items-center gap-3 rounded-2xl border border-kin-green/18 bg-gradient-to-r from-kin-green/8 to-kin-lavender/8 px-4 py-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-kin-green/14 text-kin-green-dark">
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              >
                <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
                <path d="M6 10a6 6 0 0 0 12 0" />
                <path d="M12 16v6" />
                <path d="M8 22h8" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-kin-black">AI Agent</p>
              <p className="text-xs text-kin-muted">Reads, reasons, produces results</p>
            </div>
          </div>

          {/* Connector */}
          <div className="flex flex-col items-center gap-0.5">
            <div className="h-4 w-px bg-kin-olive/20" />
            <svg viewBox="0 0 12 8" className="h-2 w-3 text-kin-olive/30">
              <path d="M1 1l5 5 5-5" stroke="currentColor" fill="none" strokeWidth="1.5" />
            </svg>
          </div>

          {/* Your data layer */}
          <div className="flex w-full max-w-md items-center gap-3 rounded-2xl border border-kin-olive/14 bg-kin-white/80 px-4 py-3 backdrop-blur-sm">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-kin-gold/14 text-kin-gold-dark">
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-kin-black">Your Vault</p>
              <p className="text-xs text-kin-muted">Private, encrypted, you control access</p>
            </div>
          </div>
        </div>

        {/* Output */}
        <div className="mx-auto mt-6 max-w-md">
          <div className="flex flex-col items-center gap-0.5">
            <svg viewBox="0 0 12 8" className="h-2 w-3 text-kin-olive/30">
              <path d="M1 1l5 5 5-5" stroke="currentColor" fill="none" strokeWidth="1.5" />
            </svg>
            <div className="h-3 w-px bg-kin-olive/20" />
          </div>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {[
              { label: "Dashboard", value: "Scores and trends" },
              { label: "Chat", value: "Scoped follow-up" },
              { label: "Proposals", value: "Trackers, articles" },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-kin-olive/12 bg-kin-white/82 px-3 py-2.5 text-center"
              >
                <p className="text-xs font-semibold text-kin-black">{item.label}</p>
                <p className="mt-0.5 text-[10px] text-kin-muted">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function SkillsPage() {
  return (
    <div className="px-6 pb-16 pt-10 sm:px-8 sm:pb-20 sm:pt-14">
      {/* Hero */}
      <section className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <Badge variant="lavender">New Concept</Badge>
          <h1 className="mt-4 font-serif text-5xl font-bold leading-[0.95] tracking-tight text-kin-black sm:text-6xl md:text-7xl">
            A perspective, grounded in your data.
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-kin-body/92">
            A skill is an AI agent loaded with a specific author's methodology, knowledge, and point
            of view. It reads the parts of your vault you choose to share, helps you make sense of
            what you're seeing, and gives you a perspective to explore.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/marketplace">
              <Button size="large">Browse Skills</Button>
            </Link>
            <a href={SIGNUP_FORM_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" size="large">
                Create an Account
              </Button>
            </a>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Badge className="normal-case tracking-normal">Purpose-built agents</Badge>
            <Badge variant="lavender" className="normal-case tracking-normal">
              Named authors
            </Badge>
            <Badge variant="gold" className="normal-case tracking-normal">
              Private by default
            </Badge>
          </div>
        </div>

        {/* Hero visual */}
        <article className="relative h-[34rem]">
          <div className="kin-surface-strong kin-surface-clouds absolute inset-0 overflow-hidden rounded-[36px] p-5 sm:p-6">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(65% 56% at 82% 12%, rgba(175,82,222,0.25) 0%, rgba(175,82,222,0) 72%), radial-gradient(55% 45% at 12% 88%, rgba(52,199,89,0.2) 0%, rgba(52,199,89,0) 70%)",
              }}
            />
            <img
              src="/skill-hero-expert.webp"
              alt="Author reviewing health data"
              className="absolute left-6 top-6 h-52 w-[55%] rounded-[22px] object-cover shadow-[0_24px_40px_-24px_rgba(27,29,26,0.8)]"
            />
            <img
              src="/skill-hero-book.webp"
              alt="Health methodology as a book"
              className="absolute bottom-24 right-5 h-36 w-[50%] rounded-[22px] object-cover shadow-[0_20px_34px_-22px_rgba(27,29,26,0.75)]"
            />
            <img
              src="/skill-hero-personal.webp"
              alt="Personal health data on a phone"
              className="absolute bottom-6 left-8 h-40 w-40 rounded-[22px] object-cover shadow-[0_22px_36px_-24px_rgba(27,29,26,0.78)]"
            />
            <div className="absolute right-6 top-64 w-44 rounded-2xl border border-kin-olive/14 bg-kin-white/90 p-3 backdrop-blur-sm">
              <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-kin-lavender">
                Skill View
              </p>
              <p className="mt-1 text-sm font-semibold text-kin-body">30 nights, one perspective</p>
              <p className="mt-0.5 text-xs text-kin-muted">3 notes ready</p>
            </div>
          </div>
        </article>
      </section>

      {/* Not a chatbot */}
      <section className="mx-auto mt-20 max-w-6xl">
        <SectionHeading
          eyebrow="What Makes It Different"
          title="Not a chatbot. Not a template. A point of view."
          text="Most AI tools give you a general-purpose conversation. A skill is the opposite: a purpose-built agent with a named author, a declared methodology, and a scoped view of your data. It's opinionated by design."
          align="center"
        />

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {[
            {
              title: "Generic AI Chat",
              items: [
                "Anonymous, no author",
                "Answers anything, knows nothing deeply",
                "Sees whatever you paste in",
                "No persistent context",
              ],
              tone: "border-kin-olive/14 bg-kin-white/60",
              label: "Typical",
              labelTone: "solidOlive" as const,
            },
            {
              title: "Static Report",
              items: [
                "One-time snapshot",
                "Template-driven, not personalized",
                "No follow-up conversation",
                "Cannot propose next steps",
              ],
              tone: "border-kin-olive/14 bg-kin-white/60",
              label: "Typical",
              labelTone: "solidOlive" as const,
            },
            {
              title: "Kin Skill",
              items: [
                "Named author, specific methodology",
                "Purpose-built for one domain",
                "You control data access",
                "Views, chat, and proposals",
              ],
              tone: "border-kin-green/20 bg-gradient-to-br from-kin-green-light/30 to-kin-lavender-light/20",
              label: "Different",
              labelTone: "default" as const,
            },
          ].map((col) => (
            <article key={col.title} className={`kin-surface rounded-3xl border p-6 ${col.tone}`}>
              <Badge variant={col.labelTone}>{col.label}</Badge>
              <h3 className="mt-3 text-xl font-semibold text-kin-black">{col.title}</h3>
              <ul className="mt-4 space-y-2.5">
                {col.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-kin-body/90">
                    <span className="mt-1.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-kin-olive/40" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* How it works + diagram */}
      <section className="mx-auto mt-20 grid max-w-6xl gap-6 lg:grid-cols-[1fr_1.05fr]">
        <div>
          <SectionHeading
            eyebrow="How It Works"
            title="Choose. Run. Understand. Ask."
            text="Four steps from install to a useful perspective. No configuration, no prompt engineering, no data wrangling."
          />
          <div className="mt-6 space-y-3">
            {howItFeels.map((item, index) => (
              <article
                key={item.step}
                className={`kin-surface rounded-2xl p-4 ${
                  index === 0
                    ? "kin-surface-clouds"
                    : index === 1
                      ? "kin-surface-waves"
                      : index === 2
                        ? "kin-surface-splash"
                        : "kin-surface-clouds"
                }`}
              >
                <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-kin-muted">
                  Step {item.step}
                </p>
                <p className="mt-1 text-sm font-semibold text-kin-black">{item.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-kin-body/90">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
        <SkillAgentDiagram />
      </section>

      {/* Trust */}
      <section className="mx-auto mt-20 max-w-6xl">
        <SectionHeading
          eyebrow="Trust"
          title="You choose whose lens you want on your data."
          text="A skill is a perspective you opt into. You pick the author, you control the data, you approve every action. The trust is yours to grant and yours to revoke."
          align="center"
        />
        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {trustPrinciples.map((item, index) => (
            <article
              key={item.title}
              className={`kin-surface rounded-3xl p-6 ${
                index === 0
                  ? "kin-surface-clouds"
                  : index === 1
                    ? "kin-surface-splash"
                    : "kin-surface-waves"
              }`}
            >
              <h3 className="text-xl font-semibold text-kin-black">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-kin-body/90">{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Second opinions */}
      <section className="mx-auto mt-20 max-w-6xl">
        <div className="kin-surface kin-surface-clouds relative overflow-hidden rounded-[34px] p-7 sm:p-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(175,82,222,0.15) 0%, rgba(255,255,255,0.86) 36%, rgba(52,199,89,0.12) 68%, rgba(255,204,0,0.15) 100%)",
            }}
          />
          <div className="relative grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div>
              <Badge variant="lavender">Multiple Perspectives</Badge>
              <h2 className="mt-4 font-serif text-3xl font-bold leading-tight text-kin-black sm:text-4xl">
                Multiple perspectives without repeating your story.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-kin-body/90">
                Run multiple skills on the same data. A sleep specialist and a longevity protocol. A
                nutrition lens and a hormone lens. Different lenses, same vault. Compare
                perspectives side by side and decide what resonates.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {exampleSkills.map((skill) => (
                <div
                  key={skill.name}
                  className="rounded-2xl border border-kin-olive/12 bg-kin-white/82 p-4 backdrop-blur-sm"
                >
                  <Badge variant={skill.badgeTone}>{skill.category}</Badge>
                  <p className="mt-2.5 text-sm font-semibold text-kin-black">{skill.name}</p>
                  <p className="mt-0.5 text-xs text-kin-muted">{skill.author}</p>
                  <p className="mt-2 text-xs leading-relaxed text-kin-body/80">
                    {skill.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* For Creators */}
      <section className="mx-auto mt-20 max-w-6xl">
        <SectionHeading
          eyebrow="For Creators"
          title="Your book, but it runs."
          text="If you have a health methodology, you can package it as a skill. Your readers don't just read your ideas. They explore them through their own data."
          align="center"
        />

        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {creatorFeatures.map((item, index) => (
            <article
              key={item.title}
              className={`kin-surface rounded-3xl p-6 ${
                index === 0
                  ? "kin-surface-splash"
                  : index === 1
                    ? "kin-surface-clouds"
                    : "kin-surface-waves"
              }`}
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-kin-olive/14 bg-kin-white text-kin-body">
                {item.icon}
              </span>
              <h3 className="mt-4 text-xl font-semibold text-kin-black">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-kin-body/90">{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Future: Scheduling */}
      <section className="mx-auto mt-20 max-w-6xl">
        <div className="kin-surface kin-surface-waves rounded-3xl p-7 sm:p-10">
          <div className="mx-auto max-w-2xl text-center">
            <Badge variant="gold">Coming Soon</Badge>
            <h2 className="mt-4 font-serif text-3xl font-bold leading-tight text-kin-black sm:text-4xl">
              Skills you can revisit when new data arrives.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-kin-body/90">
              Today, you run skills when you want. Soon, the app can show when newly added data
              gives a skill more to work with, so you can decide when to revisit it.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto mt-20 max-w-6xl">
        <article className="kin-surface-strong relative overflow-hidden rounded-[34px] p-7 sm:p-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(175,82,222,0.35) 0%, rgba(255,255,255,0.86) 36%, rgba(52,199,89,0.25) 68%, rgba(255,204,0,0.2) 100%)",
            }}
          />
          <div className="relative grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                Get Started
              </p>
              <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
                Choose the lens. Explore your data. Draw your own conclusions.
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-kin-body/92 sm:text-lg">
                Start with your vault, choose the skills that match your goals, and let named
                authors help you navigate what you're seeing privately.
              </p>
              <p className="mt-4 text-xs text-kin-muted">
                Skills provide AI-generated educational commentary, not medical advice.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:flex-col lg:items-end">
              <a href={SIGNUP_FORM_URL} target="_blank" rel="noopener noreferrer">
                <Button size="large">Create an Account</Button>
              </a>
              <Link to="/marketplace">
                <Button variant="secondary" size="large">
                  Browse Skills
                </Button>
              </Link>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
}
